import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import Toast from "@/components/base/Toast";
import { movies } from "@/mocks/movies";
import { useFavorites } from "@/hooks/useFavorites";
import { setPageMeta } from "@/utils/seo";

export default function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isFavorite, toggle } = useFavorites();
  const [activeEpisode, setActiveEpisode] = useState(1);
  const [demoOpen, setDemoOpen] = useState(false);
  const [toast, setToast] = useState("");

  const movie = useMemo(
    () => movies.find((item) => String(item.id) === String(id)),
    [id],
  );

  const closeToast = useCallback(() => setToast(""), []);

  useEffect(() => {
    setActiveEpisode(1);
    setDemoOpen(false);
    if (movie) {
      setPageMeta(
        `${movie.title}－在线观看－日韩中文字幕在线观看`,
        `${movie.title}（${movie.year}·${movie.region}·${movie.type}）剧情简介：${movie.synopsis} 全程中文字幕，观看更轻松。`,
      );
    }
  }, [movie]);

  const related = useMemo(() => {
    if (!movie) return [];
    const sameGenre = movies.filter(
      (item) =>
        item.id !== movie.id &&
        item.genres.some((genre) => movie.genres.includes(genre)),
    );
    const sameType = movies.filter(
      (item) => item.id !== movie.id && item.type === movie.type,
    );
    const merged = [...sameGenre, ...sameType].filter(
      (item, index, arr) => arr.findIndex((x) => x.id === item.id) === index,
    );
    return merged.sort((a, b) => b.popularity - a.popularity).slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-warning-line text-2xl" />
          </span>
          <h1 className="mt-5 text-2xl font-medium text-foreground-950">404</h1>
          <p className="mt-2 text-sm text-foreground-600">
            抱歉，没有找到这部影视资源，它可能已被移除。
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600"
          >
            返回首页
          </button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const favorited = isFavorite(movie.id);
  const categoryKey = movie.type === "电视剧" ? "drama" : "movie";

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制，快分享给朋友吧");
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-7xl px-4 pt-6 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: movie.type, to: `/category/${categoryKey}` },
              { label: movie.title },
            ]}
          />
        </div>

        <div className="relative mt-5">
          <div className="relative h-52 w-full overflow-hidden md:h-72">
            <img
              src={movie.backdrop || movie.cover}
              alt={`${movie.title} 剧照`}
              title={`${movie.title} 在线观看`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-foreground-950/70 to-foreground-950/60" />
          </div>

          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <div className="-mt-24 flex flex-col gap-6 md:-mt-32 md:flex-row md:gap-8">
              <div className="w-32 shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-100 sm:w-40 md:w-52">
                <div className="aspect-[2/3] w-full">
                  <img
                    src={movie.cover}
                    alt={`${movie.title} 海报`}
                    title={`${movie.title} 在线观看`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="min-w-0 flex-1 md:pt-28">
                <h1 className="text-2xl font-medium text-foreground-950 md:text-3xl">
                  {movie.title}
                </h1>
                <p className="mt-1.5 text-sm text-foreground-600">{movie.originalTitle}</p>

                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                  <span className="flex items-center gap-1 rounded-md bg-secondary-100 px-2.5 py-1 text-secondary-900">
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-star-fill text-sm" />
                    </span>
                    {movie.rating.toFixed(1)}
                  </span>
                  <span className="rounded-md bg-background-100 px-2.5 py-1 text-foreground-700">
                    {movie.year}
                  </span>
                  <span className="rounded-md bg-background-100 px-2.5 py-1 text-foreground-700">
                    {movie.region}
                  </span>
                  <span className="rounded-md bg-primary-100 px-2.5 py-1 text-primary-800">
                    {movie.type}
                  </span>
                  {movie.episodes && (
                    <span className="rounded-md bg-background-100 px-2.5 py-1 text-foreground-700">
                      全 {movie.episodes} 集
                    </span>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {movie.genres.map((genre) => (
                    <span
                      key={genre}
                      className="rounded-full border border-background-200 px-3 py-1 text-xs text-foreground-600"
                    >
                      {genre}
                    </span>
                  ))}
                </div>

                <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                  <span className="text-foreground-500">导演：</span>
                  {movie.director}
                  <span className="ml-4 text-foreground-500">主演：</span>
                  {movie.cast.join(" / ")}
                </p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setDemoOpen(true)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-accent-600"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-play-fill text-base" />
                    </span>
                    播放演示
                  </button>
                  <button
                    type="button"
                    onClick={() => toggle(movie.id)}
                    className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm transition-colors ${
                      favorited
                        ? "border-accent-300 bg-accent-50 text-accent-700"
                        : "border-background-300 text-foreground-800 hover:bg-background-100"
                    }`}
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className={favorited ? "ri-heart-fill text-base" : "ri-heart-line text-base"} />
                    </span>
                    {favorited ? "已收藏" : "收藏本片"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-share-line text-base" />
                    </span>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
          <section>
            <h2 className="flex items-center gap-2 text-lg font-medium text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              剧情简介
            </h2>
            <p className="mt-4 max-w-4xl text-sm leading-relaxed text-foreground-700 md:text-base">
              {movie.synopsis}
            </p>
          </section>

          <section className="mt-10">
            <h2 className="flex items-center gap-2 text-lg font-medium text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              在线播放演示
            </h2>
            <div className="relative mt-4 aspect-video w-full overflow-hidden rounded-lg border border-background-200 bg-foreground-950">
              <img
                src={movie.backdrop || movie.cover}
                alt={`${movie.title} 播放区域`}
                title={`${movie.title} 播放演示`}
                className="h-full w-full object-cover object-top opacity-45"
              />
              {!demoOpen && (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <button
                    type="button"
                    aria-label="播放演示"
                    onClick={() => setDemoOpen(true)}
                    className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-background-50 transition-transform hover:scale-105 md:h-20 md:w-20"
                  >
                    <i className="ri-play-fill text-3xl md:text-4xl" />
                  </button>
                  <p className="mt-4 text-sm text-background-100/85">
                    点击开始播放（演示）
                  </p>
                </div>
              )}
              {demoOpen && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-foreground-950/85 px-6 text-center">
                  <span className="flex h-12 w-12 items-center justify-center text-secondary-400">
                    <i className="ri-loader-4-line animate-spin text-3xl" />
                  </span>
                  <p className="mt-4 text-base text-background-50">播放演示</p>
                  <p className="mt-2 max-w-md text-sm text-background-100/85">
                    实际播放接口待接入，本区域仅用于界面交互演示，暂不支持真实播放。
                  </p>
                  <button
                    type="button"
                    onClick={() => setDemoOpen(false)}
                    className="mt-5 whitespace-nowrap rounded-md border border-background-50/40 px-4 py-2 text-sm text-background-50 hover:bg-background-50/10"
                  >
                    关闭演示
                  </button>
                </div>
              )}
            </div>
          </section>

          {movie.episodes && movie.episodes > 0 && (
            <section className="mt-10">
              <h2 className="flex items-center gap-2 text-lg font-medium text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                剧集列表
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {Array.from({ length: movie.episodes }).map((_, index) => {
                  const ep = index + 1;
                  return (
                    <button
                      key={ep}
                      type="button"
                      onClick={() => setActiveEpisode(ep)}
                      className={`h-10 min-w-[44px] whitespace-nowrap rounded-md px-3 text-sm transition-colors ${
                        ep === activeEpisode
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      第 {ep} 集
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5">
                <p className="text-sm font-medium text-foreground-950">
                  第 {activeEpisode} 集 · 本集预告说明
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {movie.title} 第 {activeEpisode} 集正处于剧情推进的关键阶段，人物关系与主线矛盾进一步展开。当前为演示内容，点击不同剧集会更新本区域说明，暂不支持真实视频切换。
                </p>
              </div>
            </section>
          )}

          <section className="mt-10">
            <div className="flex flex-wrap gap-3">
              <Link
                to={`/category/${categoryKey}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-800 hover:bg-background-100"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-layout-grid-line text-base" />
                </span>
                查看所属分类：{movie.type}
              </Link>
              <button
                type="button"
                onClick={() => setToast("演示功能：社交平台分享暂未接入")}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-800 hover:bg-background-100"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-wechat-line text-base" />
                </span>
                分享到社交平台（演示）
              </button>
            </div>
          </section>

          {related.length > 0 && (
            <section className="mt-12">
              <h2 className="flex items-center gap-2 text-lg font-medium text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                相关推荐
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                {related.map((item) => (
                  <MovieCard
                    key={item.id}
                    movie={item}
                    isFavorite={isFavorite(item.id)}
                    onToggleFavorite={toggle}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      {toast && <Toast message={toast} onClose={closeToast} />}
      <SiteFooter />
    </div>
  );
}